"use client";

import { useCallback, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { ArrowLeft, ArrowRight, ArrowUpRight, RotateCw } from "lucide-react";
import { resolveProfileLink, sortProfileLinks } from "@/lib/link-types";
import {
  LinkActionIcon,
  ProfileLinkItem,
  ThemeAttribution,
  ThemeFooter,
  ThemeSectionBody,
  type ThemeRendererProps
} from "./theme-primitives";

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return reduced;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function classifyRawLine(line: string): "heading" | "quote" | "fence" | "list" | "table" | "rule" | "blank" | "text" {
  const trimmed = line.trim();
  if (!trimmed) return "blank";
  if (/^#{1,6}\s/.test(trimmed)) return "heading";
  if (/^```|^~~~/.test(trimmed)) return "fence";
  if (/^>/.test(trimmed)) return "quote";
  if (/^\|.*\|$/.test(trimmed)) return "table";
  if (/^(?:---|\*\*\*|___)\s*$/.test(trimmed)) return "rule";
  if (/^(?:[-*+]|\d+\.)\s/.test(trimmed)) return "list";
  return "text";
}

export function RawTheme({ model, interactiveLinks, onLinkClick, showAttribution, t }: ThemeRendererProps) {
  const links = sortProfileLinks(model.profile.links);
  const sparseClass = model.sections.length === 0 && links.length === 0 ? " theme-v2-sparse" : "";
  const rawLines = model.profile.bodyMarkdown ? model.profile.bodyMarkdown.split("\n") : [];

  return (
    <article className={`theme-v2-wrapper theme-v2-chrome${sparseClass}`} data-theme="chrome">
      <div className="raw-sheet">
        <header className="raw-header">
          <div className="raw-doc-bar">
            <code className="raw-address">{"<!-- 30s.to/@"}{model.profile.handle || "handle"}{" -->"}</code>
            <span className="raw-spec-badge" aria-hidden="true">MARKDOWN / UTF-8 · {model.reading.seconds || 30}S</span>
          </div>
          <div className="raw-title-line">
            <span className="raw-source-mark" aria-hidden="true">#</span>
            <h1>{model.profile.displayName || t("theme.fallback.name")}</h1>
          </div>
          <p className="raw-identity">
            <span className="raw-control" aria-hidden="true">{">"}</span>
            <span className="raw-identity-copy">{model.profile.oneLineIdentity || t("theme.fallback.identity")}</span>
          </p>
        </header>

        {model.profile.tags.length > 0 && (
          <ul className="raw-tags" aria-label={t("theme.tags")}>
            {model.profile.tags.map((tag) => (
              <li key={tag}><span aria-hidden="true">- </span><code>`#{tag}`</code></li>
            ))}
          </ul>
        )}

        {model.profile.bodyMarkdown && (
          <div className="raw-content">
            <section className="raw-section">
              <div className="raw-editor-bar" aria-hidden="true">
                <span>README.md</span>
                <span>{rawLines.length} LINES · LITERAL SOURCE</span>
              </div>
              <pre>
                <code>
                  {rawLines.map((line, index) => (
                    <span
                      key={index}
                      className={`raw-line raw-line-${classifyRawLine(line)}`}
                      data-line={String(index + 1).padStart(2, "0")}
                    >
                      {line}
                      {index < rawLines.length - 1 ? "\n" : ""}
                    </span>
                  ))}
                </code>
              </pre>
            </section>
          </div>
        )}

        {links.length > 0 && (
          <nav className="raw-links" aria-label={t("theme.connect")}>
            <h2><span className="raw-control" aria-hidden="true">## </span>{t("theme.connect")}</h2>
            {links.map((link) => {
              const resolved = resolveProfileLink(link);
              const target = resolved.href ?? `copy:${resolved.copyText ?? link.value}`;
              return (
                <ProfileLinkItem key={link.id} link={link} interactiveLinks={interactiveLinks} onLinkClick={onLinkClick} className="raw-link">
                  <code><span aria-hidden="true">- </span>[{link.label}]({target})</code>
                </ProfileLinkItem>
              );
            })}
          </nav>
        )}

        {showAttribution && <footer className="raw-footer">{"<!-- "}<ThemeAttribution t={t} />{" -->"}</footer>}
      </div>
    </article>
  );
}

export function PixelTheme({ model, interactiveLinks, onLinkClick, showAttribution, t }: ThemeRendererProps) {
  const links = sortProfileLinks(model.profile.links);
  const deckRef = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const [activeScreen, setActiveScreen] = useState(0);
  const screenCount = model.sections.length + 2;
  const sparseClass = model.sections.length === 0 && links.length === 0 ? " theme-v2-sparse" : "";

  const goToScreen = useCallback((index: number) => {
    const deck = deckRef.current;
    if (!deck) return;
    const next = clamp(index, 0, screenCount - 1);
    const screen = deck.querySelectorAll<HTMLElement>("[data-pixel-screen]")[next];
    if (!screen) return;
    deck.scrollTo({ left: screen.offsetLeft, behavior: reduced ? "auto" : "smooth" });
    setActiveScreen(next);
  }, [reduced, screenCount]);

  const handleScroll = useCallback(() => {
    const deck = deckRef.current;
    if (!deck || deck.clientWidth === 0) return;
    setActiveScreen(clamp(Math.round(deck.scrollLeft / deck.clientWidth), 0, screenCount - 1));
  }, [screenCount]);

  useEffect(() => {
    const deck = deckRef.current;
    if (!deck) return;
    const handleHorizontalWheel = (event: WheelEvent) => {
      if (event.ctrlKey || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      const maxScroll = Math.max(0, deck.scrollWidth - deck.clientWidth);
      const atStart = deck.scrollLeft <= 1;
      const atEnd = deck.scrollLeft >= maxScroll - 1;
      if ((event.deltaX < 0 && atStart) || (event.deltaX > 0 && atEnd)) return;

      event.preventDefault();
      const multiplier = event.deltaMode === WheelEvent.DOM_DELTA_LINE
        ? 16
        : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
          ? deck.clientWidth
          : 1;
      deck.scrollLeft += event.deltaX * multiplier;
    };
    deck.addEventListener("wheel", handleHorizontalWheel, { passive: false });
    return () => deck.removeEventListener("wheel", handleHorizontalWheel);
  }, []);

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const target = event.target as HTMLElement;
    if (target.closest("a, button, input, textarea, select, [contenteditable='true']")) return;
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      goToScreen(activeScreen - 1);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      goToScreen(activeScreen + 1);
    } else if (event.key === "Home") {
      event.preventDefault();
      goToScreen(0);
    } else if (event.key === "End") {
      event.preventDefault();
      goToScreen(screenCount - 1);
    }
  };

  return (
    <article className={`theme-v2-wrapper theme-v2-pixel${sparseClass}`} data-theme="pixel">
      <div className="pixel-device">
        <header className="pixel-device-bar" aria-hidden="true">
          <span>30S-BOY</span><i /><strong>ONLINE</strong>
        </header>
        <div className="pixel-screen-frame">
          <div className="pixel-scanlines" aria-hidden="true" />
          <div className="pixel-deck" ref={deckRef} onScroll={handleScroll} onKeyDown={handleKeyDown} tabIndex={0} role="region" aria-label={t("theme.pixel.screen", { current: activeScreen + 1, total: screenCount })}>
            <section className="pixel-screen pixel-identity-screen" data-pixel-screen data-screen-index="0">
              <div className="pixel-screen-scroll">
                <div className="pixel-address">30s.to/@{model.profile.handle || "handle"}</div>
                <span className="pixel-level-label">PLAYER / 01</span>
                <h1>{model.profile.displayName || t("theme.fallback.name")}</h1>
                <p>{model.profile.oneLineIdentity || t("theme.fallback.identity")}</p>
                {model.profile.tags.length > 0 && (
                  <div className="pixel-tags">
                    {model.profile.tags.map((tag) => <span key={tag}>+ {tag}</span>)}
                  </div>
                )}
                <div className="pixel-start" aria-hidden="true">PRESS →</div>
              </div>
            </section>

            {model.sections.map((section, index) => (
              <section className="pixel-screen pixel-content-screen" data-pixel-screen data-screen-index={index + 1} key={section.id}>
                <div className="pixel-screen-scroll">
                  <div className="pixel-screen-meta"><span>STAGE {String(index + 1).padStart(2, "0")}</span><i>{index + 2}/{screenCount}</i></div>
                  <h2>{section.title}</h2>
                  <ThemeSectionBody
                    section={section}
                    prefix="pixel"
                    proseClassName="pixel-copy"
                    entryBadge={(itemIndex) => `ITEM ${String(itemIndex + 1).padStart(2, "0")}`}
                  />
                </div>
              </section>
            ))}

            <section className="pixel-screen pixel-links-screen" data-pixel-screen data-screen-index={screenCount - 1}>
              <div className="pixel-screen-scroll">
                <div className="pixel-screen-meta"><span>FINAL STAGE</span><i>{screenCount}/{screenCount}</i></div>
                {links.length > 0 && (
                  <nav className="pixel-links" aria-label={t("theme.connect")}>
                    <h2>{t("theme.connect")}</h2>
                    {links.map((link, index) => (
                      <ProfileLinkItem key={link.id} link={link} interactiveLinks={interactiveLinks} onLinkClick={onLinkClick} className="pixel-link">
                        <span><i>{String(index + 1).padStart(2, "0")}</i>{link.label}</span>
                        <ArrowUpRight size={16} />
                      </ProfileLinkItem>
                    ))}
                  </nav>
                )}
                <ThemeFooter showAttribution={showAttribution} t={t} />
              </div>
            </section>
          </div>
        </div>

        <div className="pixel-controls">
          <button type="button" onClick={() => goToScreen(activeScreen - 1)} disabled={activeScreen === 0} aria-label={t("theme.pixel.previous")}><ArrowLeft /></button>
          <span aria-live="polite">
            <strong>{String(activeScreen + 1).padStart(2, "0")} / {String(screenCount).padStart(2, "0")}</strong>
            <i className="pixel-pips" aria-hidden="true">
              {Array.from({ length: screenCount }, (_, index) => (
                <b key={index} data-active={index === activeScreen ? "true" : undefined} />
              ))}
            </i>
          </span>
          <button type="button" onClick={() => goToScreen(activeScreen + 1)} disabled={activeScreen === screenCount - 1} aria-label={t("theme.pixel.next")}><ArrowRight /></button>
        </div>
      </div>
    </article>
  );
}

export function ArcadeTheme({ model, interactiveLinks, onLinkClick, showAttribution, t }: ThemeRendererProps) {
  const links = sortProfileLinks(model.profile.links);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const [activeStage, setActiveStage] = useState(0);
  const [progress, setProgress] = useState(0);
  const stageCount = model.sections.length + (links.length > 0 ? 3 : 2);
  const stageSignature = `${model.sections.map((section) => section.id).join("|")}:${links.length > 0}`;
  const sparseClass = model.sections.length === 0 && links.length === 0 ? " theme-v2-sparse" : "";

  const goToStage = useCallback((index: number) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const stages = scroller.querySelectorAll<HTMLElement>("[data-arcade-stage]");
    const next = stages[clamp(index, 0, stages.length - 1)];
    if (!next) return;
    scroller.scrollTo({ top: next.offsetTop, behavior: reduced ? "auto" : "smooth" });
  }, [reduced]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const stages = Array.from(scroller.querySelectorAll<HTMLElement>("[data-arcade-stage]"));
    let frame = 0;
    const updateScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const max = Math.max(1, scroller.scrollHeight - scroller.clientHeight);
        setProgress(clamp(scroller.scrollTop / max, 0, 1));
        if (typeof IntersectionObserver === "undefined") {
          const midpoint = scroller.scrollTop + scroller.clientHeight / 2;
          const current = stages.reduce((best, stage, index) => Math.abs(stage.offsetTop - midpoint) < Math.abs(stages[best].offsetTop - midpoint) ? index : best, 0);
          setActiveStage(current);
        }
      });
    };
    const observer = typeof IntersectionObserver === "undefined" ? null : new IntersectionObserver((entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (visible) setActiveStage(Number((visible.target as HTMLElement).dataset.arcadeStage || 0));
    }, { root: scroller, threshold: [0.25, 0.5, 0.75] });
    stages.forEach((stage) => observer?.observe(stage));
    scroller.addEventListener("scroll", updateScroll, { passive: true });
    updateScroll();
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      scroller.removeEventListener("scroll", updateScroll);
    };
  }, [stageCount, stageSignature]);

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const target = event.target as HTMLElement;
    if (target.closest("a, button, input, textarea, select, [contenteditable='true']")) return;
    if (event.key === "PageDown") {
      event.preventDefault();
      goToStage(activeStage + 1);
    } else if (event.key === "PageUp") {
      event.preventDefault();
      goToStage(activeStage - 1);
    } else if (event.key === "Home") {
      event.preventDefault();
      goToStage(0);
    } else if (event.key === "End") {
      event.preventDefault();
      goToStage(stageCount - 1);
    }
  };

  let stageIndex = 0;
  const heroIndex = stageIndex++;
  const sectionStart = stageIndex;
  stageIndex += model.sections.length;
  const linksIndex = links.length > 0 ? stageIndex++ : -1;
  const footerIndex = stageIndex;

  return (
    <article className={`theme-v2-wrapper theme-v2-arcade${sparseClass}`} data-theme="arcade">
      <div className="arcade-cabinet">
        <div className="arcade-hud">
          <span>{t("theme.arcade.stage", { current: activeStage + 1, total: stageCount })}</span>
          <strong>30S / SCORE {String(Math.round(progress * 9999)).padStart(4, "0")}</strong>
          <div><i style={{ width: `${Math.max(2, progress * 100)}%` }} /></div>
        </div>
        <div className="arcade-scroller" ref={scrollerRef} onKeyDown={handleKeyDown} tabIndex={0} role="region" aria-label={t("theme.arcade.stage", { current: activeStage + 1, total: stageCount })}>
          <section className="arcade-stage arcade-start" data-arcade-stage={heroIndex}>
            <div className="arcade-stars" aria-hidden="true"><i /><i /><i /><i /></div>
            <span className="arcade-stage-label">START / PLAYER ONE</span>
            <div className="arcade-address">30s.to/@{model.profile.handle || "handle"}</div>
            <h1>{model.profile.displayName || t("theme.fallback.name")}</h1>
            <p>{model.profile.oneLineIdentity || t("theme.fallback.identity")}</p>
            {model.profile.tags.length > 0 && (
              <div className="arcade-tags">{model.profile.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
            )}
            <button type="button" className="arcade-start-button" onClick={() => goToStage(1)}>START <ArrowRight size={18} /></button>
          </section>

          {model.sections.map((section, index) => (
            <section className={`arcade-stage arcade-level arcade-level-${index % 3}`} data-arcade-stage={sectionStart + index} key={section.id}>
              <span className="arcade-stage-label">LEVEL {String(index + 1).padStart(2, "0")}</span>
              <div className="arcade-level-number" aria-hidden="true">{index + 1}</div>
              <h2>{section.title}</h2>
              <ThemeSectionBody
                section={section}
                prefix="arcade"
                proseClassName="arcade-copy"
                entryBadge={(itemIndex) => `1UP · ${String(itemIndex + 1).padStart(2, "0")}`}
              />
            </section>
          ))}

          {links.length > 0 && (
            <section className="arcade-stage arcade-bonus" data-arcade-stage={linksIndex}>
              <span className="arcade-stage-label">BONUS / STAGE SELECT</span>
              <h2>{t("theme.connect")}</h2>
              <nav className="arcade-links" aria-label={t("theme.connect")}>
                {links.map((link, index) => (
                  <ProfileLinkItem key={link.id} link={link} interactiveLinks={interactiveLinks} onLinkClick={onLinkClick} className="arcade-link">
                    <i>{String(index + 1).padStart(2, "0")}</i><span>{link.label}</span><ArrowUpRight size={16} />
                  </ProfileLinkItem>
                ))}
              </nav>
            </section>
          )}

          <section className="arcade-stage arcade-end" data-arcade-stage={footerIndex}>
            <span className="arcade-stage-label">END SCREEN</span>
            <div className="arcade-end-mark" aria-hidden="true">30S</div>
            <ThemeFooter showAttribution={showAttribution} t={t} />
            <button type="button" onClick={() => goToStage(0)}>REPLAY <RotateCw size={17} /></button>
          </section>
        </div>
      </div>
    </article>
  );
}

export function NoiseTheme({ model, interactiveLinks, onLinkClick, showAttribution, t }: ThemeRendererProps) {
  const links = sortProfileLinks(model.profile.links);
  const rootRef = useNoiseScroll();
  const [seed, setSeed] = useState(0);
  const sparseClass = model.sections.length === 0 && links.length === 0 ? " theme-v2-sparse" : "";

  return (
    <article ref={rootRef} className={`theme-v2-wrapper theme-v2-noise${sparseClass}`} data-theme="noise" data-noise-seed={seed}>
      <div className="noise-static" aria-hidden="true" />
      <div className="noise-rail noise-rail-a" aria-hidden="true">SIGNAL / 30S / DISPLACE / SIGNAL / 30S / DISPLACE</div>
      <div className="noise-rail noise-rail-b" aria-hidden="true">{model.profile.displayName || "IDENTITY"}</div>
      <div className="noise-shell">
        <header className="noise-header">
          <div className="noise-address"><span>30S / NOISE</span><strong>30s.to/@{model.profile.handle || "handle"}</strong></div>
          <button type="button" className="noise-reseed" onClick={() => setSeed((value) => (value + 1) % 4)} aria-label={t("theme.noise.reseed")}>
            <RotateCw size={15} /> RESEED / NOISE <i>{seed + 1}:4</i>
          </button>
        </header>

        <section className="noise-hero">
          <div className="noise-echo" aria-hidden="true">{model.profile.displayName || "IDENTITY"}</div>
          <span className="noise-kicker">UNSTABLE IDENTITY / STABLE CONTENT</span>
          <h1>{model.profile.displayName || t("theme.fallback.name")}</h1>
          <p>{model.profile.oneLineIdentity || t("theme.fallback.identity")}</p>
          {model.profile.tags.length > 0 && (
            <div className="noise-tags">{model.profile.tags.map((tag, index) => <span key={tag}><i>{index + 1}</i>{tag}</span>)}</div>
          )}
        </section>

        {model.sections.length > 0 && (
          <div className="noise-content">
            {model.sections.map((section, index) => (
              <section className="noise-section" key={section.id}>
                <div className="noise-section-echo" aria-hidden="true">{String(index + 1).padStart(2, "0")}</div>
                <div className="noise-section-head"><span>BLOCK / {String(index + 1).padStart(2, "0")} · SEED 0{seed + 1}</span><h2>{section.title}</h2></div>
                <ThemeSectionBody
                  section={section}
                  prefix="noise"
                  proseClassName="noise-copy"
                  entryBadge={(itemIndex) => `[${String(itemIndex + 1).padStart(2, "0")}]`}
                />
              </section>
            ))}
          </div>
        )}

        {links.length > 0 && (
          <nav className="noise-links" aria-label={t("theme.connect")}>
            <div className="noise-links-heading"><span>OUT / {String(links.length).padStart(2, "0")}</span><strong>{t("theme.connect")}</strong></div>
            {links.map((link, index) => (
              <ProfileLinkItem key={link.id} link={link} interactiveLinks={interactiveLinks} onLinkClick={onLinkClick} className="noise-link">
                <i>{String(index + 1).padStart(2, "0")}</i><span>{link.label}</span><LinkActionIcon action={resolveProfileLink(link).action} link={link} />
              </ProfileLinkItem>
            ))}
          </nav>
        )}
        <ThemeFooter showAttribution={showAttribution} t={t} />
      </div>
    </article>
  );
}

function useNoiseScroll() {
  const rootRef = useRef<HTMLElement>(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    const root = rootRef.current;
    if (!root || reduced || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const previewViewport = root.closest<HTMLElement>(".preview-page-viewport");
    const target: HTMLElement | Window = previewViewport ?? window;
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const scrollTop = previewViewport?.scrollTop ?? window.scrollY;
        root.style.setProperty("--noise-scroll", `${scrollTop}px`);
        root.style.setProperty("--noise-shift", `${(scrollTop % 120) / 12}px`);
      });
    };
    target.addEventListener("scroll", update, { passive: true });
    update();
    return () => {
      cancelAnimationFrame(frame);
      target.removeEventListener("scroll", update);
    };
  }, [reduced]);

  return rootRef;
}
